import { BCMSDate, BCMSCurrency } from "@bcm-dev/bcms-vite";
import { Account, PositivePayCheck } from "model";
import TASPositivePayCheckForm from "components/pages/TASPositivePayAddChecksPage/classes/TASPositivePayCheckForm";
/**
 * @class TASPositivePayViewChecksSearch
 * @classdesc Class for controlling the search functionality of the "View Checks" page.
 * @property {ACCOUNT} _account - The account to filter by.
 * @property {ARRAY} _checkForms - The available check forms.
 * @property {ARRAY} _editCheckForms - The available editable check forms.
 * @property {ARRAY} _accounts - The list of available accounts.
 * @property {STRING} _payee - The payee name to filter by.
 * @property {ARRAY} _statuses - The list of available check statuses.
 * @property {STRING} _status - The selected check status to filter by.
 * @property {NUMBER} _checkNumberFrom - The starting check number in the search range.
 * @property {NUMBER} _checkNumberTo - The ending check number in the search range.
 * @property {BCMSDate} _issuedDateFrom - The earliest issued date in the search range.
 * @property {BCMSDate} _issuedDateTo - The latest issued date in the search range.
 * @property {NUMBER} _minimumAmount - The minimum check amount to filter by.
 * @property {NUMBER} _maximumAmount - The maximum check amount to filter by.
 */
export default class TASPositivePayViewChecksSearch {
    constructor(record) {
        this.checkForms = record.checkForms;
        this.editCheckForms = record.editCheckForms;
        this.accounts = record.accounts;
        this.account = record.account;
        this.payee = record.payee;
        this.statuses = record.statuses;
        this.status = record.status;
        this.checkNumberFrom = record.checkNumberFrom;
        this.checkNumberTo = record.checkNumberTo;
        this.issuedDateFrom = record.issuedDateFrom;
        this.issuedDateTo = record.issuedDateTo;
        this.minimumAmount = record.minimumAmount;
        this.maximumAmount = record.maximumAmount;
    }

    /**
     * @name checkForms
     * @instance
     * @property set {ARRAY} - sets the check forms of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    set checkForms(value) { this._checkForms = value; }

    /**
     * @name editCheckForms
     * @instance
     * @property set {ARRAY} - sets the editable check forms of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    set editCheckForms(value) { this._editCheckForms = value; }

    /**
     * @name accounts
     * @instance
     * @property set {STRING} - sets the accounts of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    set accounts(value) { this._accounts = value; }

    /**
     * @name account
     * @instance
     * @property set {STRING} - sets the company id
     * @memberof TASPositivePayViewChecksSearch
     */
    set account(value) { this._account = value; }

    /**
     * @name payee
     * @instance
     * @property set {STRING} - sets the payee
     * @memberof TASPositivePayViewChecksSearch
     */
    set payee(value) { this._payee = value; }

    /**
     * @name statuses
     * @instance
     * @property set {ARRAY} - sets the statuses of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    set statuses(value) { this._statuses = value; }

    /**
     * @name status
     * @instance
     * @property set {STRING} - sets the status
     * @memberof TASPositivePayViewChecksSearch
     */
    set status(value) { this._status = value; }

    /**
     * @name checkNumberFrom
     * @instance
     * @property set {STRING} - sets the checkNumberFrom
     * @memberof TASPositivePayViewChecksSearch
     */
    set checkNumberFrom(value) { this._checkNumberFrom = value; }

    /**
     * @name checkNumberTo
     * @instance
     * @property set {STRING} - sets the checkNumberTo
     * @memberof TASPositivePayViewChecksSearch
     */
    set checkNumberTo(value) { this._checkNumberTo = value; }

    /**
     * @name issuedDateFrom
     * @instance
     * @property set {STRING} - sets the issuedDate
     * @memberof TASPositivePayAddChecksPageCheckForm
     */
    set issuedDateFrom(value) {
        if(value && value instanceof BCMSDate) {
            this._issuedDateFrom = value;
        } else if(value) {
            this._issuedDateFrom = BCMSDate.init().offset(value)
        } else {
            this._issuedDateFrom = value;
        }
    }

    /**
     * @name issuedDateFrom
     * @instance
     * @property set {STRING} - sets the issuedDateTo
     * @memberof TASPositivePayViewChecksSearch
     */
    set issuedDateTo(value) {
        if(value && value instanceof BCMSDate) {
            this._issuedDateTo = value;
        } else if(value) {
            this._issuedDateTo = BCMSDate.init().offset(value)
        } else {
            this._issuedDateTo = value;
        }
    }

    /**
     * @name minimumAmount
     * @instance
     * @property set {STRING} - sets the minimumAmount
     * @memberof TASPositivePayViewChecksSearch
     */
    set minimumAmount(value) { this._minimumAmount = value; }

    /**
     * @name maximumAmount
     * @instance
     * @property set {STRING} - sets the maximumAmount
     * @memberof TASPositivePayViewChecksSearch
     */
    set maximumAmount(value) { this._maximumAmount = value; }

    /**
     * @name checkForms
     * @instance
     * @property get {ARRAY} - returns the check forms of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    get checkForms() { return this._checkForms; }

    /**
     * @name checkForms
     * @instance
     * @property get {ARRAY} - returns the editable check forms of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    get editCheckForms() { return this._editCheckForms; }

    /**
     * @name accounts
     * @instance
     * @property get {STRING} - returns the accounts of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    get accounts() { return this._accounts; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account
     * @memberof TASPositivePayViewChecksSearch
     */
    get account() { return this._account; }

    /**
     * @name payee
     * @instance
     * @property get {STRING} - returns the payee
     * @memberof TASPositivePayViewChecksSearch
     */
    get payee() { return this._payee; }

    /**
     * @name statuses
     * @instance
     * @property get {ARRAY} - returns the statuses of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    get statuses() { return this._statuses; }

    /**
     * @name status
     * @instance
     * @property get {STRING} - returns the status
     * @memberof TASPositivePayViewChecksSearch
     */
    get status() { return this._status; }

    /**
     * @name checkNumberFrom
     * @instance
     * @property get {STRING} - returns the checkNumberFrom
     * @memberof TASPositivePayViewChecksSearch
     */
    get checkNumberFrom() { return this._checkNumberFrom; }

    /**
     * @name checkNumberTo
     * @instance
     * @property get {STRING} - returns the checkNumberTo
     * @memberof TASPositivePayViewChecksSearch
     */
    get checkNumberTo() { return this._checkNumberTo; }

    /**
     * @name issuedDateFrom
     * @instance
     * @property get {BCMSDate} - returns the "issued date from" value of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    get issuedDateFrom() { return this._issuedDateFrom; }

    /**
     * @name issuedDateTo
     * @instance
     * @property get {STRING} - returns the "issued date to" value of the class.
     * @memberof TASPositivePayViewChecksSearch
     */
    get issuedDateTo() { return this._issuedDateTo; }

    /**
     * @name minimumAmount
     * @instance
     * @property get {STRING} - returns the minimumAmount
     * @memberof TASPositivePayViewChecksSearch
     */
    get minimumAmount() { return this._minimumAmount; }

    /**
     * @name maximumAmount
     * @instance
     * @property get {STRING} - returns the maximumAmount
     * @memberof TASPositivePayViewChecksSearch
     */
    get maximumAmount() { return this._maximumAmount; }


    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof TASPositivePayViewChecksSearch
     */
    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    get filters() {
        return Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && key !== "accounts") {
                result[key] = value;
            }

            return result;
        }, {});
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayViewChecksSearch
     */
    get record() {
        return {
            checkForms: this.checkForms,
            editCheckForms: this.editCheckForms,
            accounts: this.accounts,
            account: this.account,
            payee: this.payee,
            statuses: this.statuses,
            status: this.status,
            checkNumberFrom: this.checkNumberFrom,
            checkNumberTo: this.checkNumberTo,
            issuedDateFrom: this.issuedDateFrom,
            issuedDateTo: this.issuedDateTo,
            minimumAmount: this.minimumAmount,
            maximumAmount: this.maximumAmount,
        };
    }

    /**
     * @function checkValue
     * @memberof TASPositivePayViewChecksSearch
     * @instance
     * @property {Object} item - The item to evaluate.
     * @property {STRING} value - The search value to compare against.
     * @description Checks whether an item passes the current search filters.
     * @returns {BOOLEAN} True if the item passes the search filters. Otherwise, false.
     */
    checkValue(item, key) {
        switch(key) {
            case "account":
                return item.account.id === this[key].id;
            case "payee":
                return item[key].toLowerCase() === this[key].toLowerCase();
            case "status":
                return item[key].toLowerCase() === this[key].toLowerCase() || this[key] === "all";
            case "checkNumberFrom":
                return parseFloat(this[key]) <= parseFloat(item.checkNumber);
            case "checkNumberTo":
                return parseFloat(item.checkNumber) <= parseFloat(this[key]);
            case "issuedDateFrom":
                return item.issuedDate.isAfter(this[key]) || item.issuedDate.isEqual(this[key]);
            case "issuedDateTo":
                return this[key].isAfter(item.issuedDate) || item.issuedDate.isEqual(this[key]);
            case "minimumAmount":
                const minAamount = item.amount ? item.amount.amount : item.amount;
                return parseFloat(this[key]) <= parseFloat(minAamount);
            case "maximumAmount":
                const maxAmount = item.amount ? item.amount.amount : item.amount;
                return parseFloat(maxAmount) <= parseFloat(this[key]);   
            default:
                return true;
        }
    }

    /**
     * @function filter
     * @memberof TASPositivePayViewChecksSearch
     * @instance
     * @property {VOID}
     * @description returns the check forms that have pass through the search filter.
     * @returns {ARRAY}
     */
    filter() {
        const items = this.checkForms.filter(item => {
            if(!item.account) return false;
            return item.account.id === this.account.id;
        });

        return items.filter(item => {
            return Object.keys(this.filters).reduce((result, key) => {
                if(!this.checkValue(item, key)) {
                    result = false;
                }

                return result;
            }, true);
        }, []);
    }

    /**
     * @function userEditedCheckForms
     * @memberof TASPositivePayViewChecksSearch
     * @instance
     * @property {VOID}
     * @description returns an array of check forms that have been edited but not "saved" yet.
     * @returns {ARRAY}
     */
    userEditedCheckForms() {
        let differentItems = [];

        const addItem = item => {
            const itemAlreadyAdded = differentItems.filter(_item => _item.id === item.id).length > 0;
            if(!itemAlreadyAdded) {
                differentItems.push(item);
            }
        };
        
        for(let i = 0; i < this.checkForms.length; i++) {
            const currCheckFormRecord = this.checkForms[i].record;
            const currEditCheckFormRecord = this.editCheckForms[i].record;
            let same = true;

            Object.keys(currCheckFormRecord).map(key => {
                const currCheckFormVal = currCheckFormRecord[key];
                const currEditCheckFormVal = currEditCheckFormRecord[key];

                if(currCheckFormVal instanceof BCMSDate) {
                    const sameDay = currCheckFormVal.date.getFullYear() === currEditCheckFormVal.date.getFullYear()
                    && currCheckFormVal.date.getMonth() === currEditCheckFormVal.date.getMonth()
                    && currCheckFormVal.date.getDate() === currEditCheckFormVal.date.getDate();

                    if(!sameDay) {
                        same = false;
                    }

                } else if(currCheckFormVal instanceof BCMSCurrency) {
                    if(currCheckFormVal.amount !== currEditCheckFormVal.amount) {
                        same = false;
                    }
                } else if(key === "account") {
                    if(currCheckFormVal.id !== currEditCheckFormVal.id) {
                        same = false;
                    }
                } else if(currCheckFormVal !== currEditCheckFormVal) {
                    same = false;
                }

                if(!same) {
                    addItem(this.checkForms[i]);
                }
            });
        }

        return differentItems;
    }

    /**
     * @function defaultVals
     * @memberof TASPositivePayViewChecksSearch
     * @static
     * @property {VOID}
     * @description returns an object of default values for this class.
     * @returns {OBJECT}
     */
    static defaultVals() {
        const accounts = Account.initAllBusinessAssetOnly().filter(item => item.type !== "savings");
        const selectedAccount = accounts[0] ?? {};
        const statuses = [
            { id: "0", value: "all", label: "All" },
            { id: "1", value: "reconciled", label: "Reconciled" },
            { id: "2", value: "not reconciled", label: "Not Reconciled" }
        ];

        const checkForms = PositivePayCheck.initAll().reduce((acc, item) => {
            acc.push( TASPositivePayCheckForm.init(item.record));
            return acc;
        }, []);

        const editCheckForms = PositivePayCheck.initAll().reduce((acc, item) => {
            acc.push( TASPositivePayCheckForm.init(item.record));
            return acc;
        }, []);

        return { 
            checkForms: checkForms,
            editCheckForms: editCheckForms,
            accounts: accounts,
            account: selectedAccount,
            payee: undefined,
            statuses: statuses,
            status: "all",
            checkNumberFrom: undefined,
            checkNumberTo: undefined,
            issuedDateFrom: undefined,
            issuedDateTo: undefined,
            minimumAmount: undefined,
            maximumAmount: undefined,
        };
    }

    /**
     * @function reset
     * @memberof TASPositivePayViewChecksSearch
     * @instance
     * @property {VOID}
     * @description returns an instance of this class with the default values.
     * @returns {TASPositivePayViewChecksSearch}
     */
    reset() {
        const defaultVals = TASPositivePayViewChecksSearch.defaultVals();
        return TASPositivePayViewChecksSearch.init(defaultVals);
    }

    /**
     * @function resetFieldsOnly
     * @memberof TASPositivePayViewChecksSearch
     * @instance
     * @property {VOID}
     * @description returns an instance of this class with some, not all, default values.
     * The check forms and editable check forms are left alone since the user may have edited and saved
     * checks, the data of which we still want to keep.
     * @returns {TASPositivePayViewChecksSearch}
     */
    resetFieldsOnly() {
        const defaultVals = { 
            ...TASPositivePayViewChecksSearch.defaultVals(),
            checkForms: this.checkForms,
            editCheckForms: this.editCheckForms
         };
       
        return TASPositivePayViewChecksSearch.init(defaultVals);
    }

    /**
     * @function init
     * @memberof TASPositivePayViewChecksSearch
     * @instance
     * @property {VOID}
     * @description returns an instance of this class with default values
     * that can be overridden by passing an object to the argument.
     * @returns {TASPositivePayViewChecksSearch}
     */
    static init(record) {
        const defaultVals = TASPositivePayViewChecksSearch.defaultVals();
        const vals = { 
            ...defaultVals,
            ...record ?? {} 
        };

        return new TASPositivePayViewChecksSearch(vals);
    }
}